'use client'

import React, { useState, useRef, useEffect, forwardRef } from 'react'
import s from './Select.module.scss'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectOptionGroup {
  groupLabel: string
  options: SelectOption[]
}

type SelectItem = SelectOption | SelectOptionGroup

function isGroup(item: SelectItem): item is SelectOptionGroup {
  return 'groupLabel' in item
}

function flattenItems(items: SelectItem[]): SelectOption[] {
  const flat: SelectOption[] = []
  for (const item of items) {
    if (isGroup(item)) {
      flat.push(...item.options)
    } else {
      flat.push(item)
    }
  }
  return flat
}

interface SelectProps {
  label?: string
  value?: string
  onChange?: (value: string) => void
  options: SelectItem[]
  placeholder?: string
  required?: boolean
  className?: string
  error?: string
  disabled?: boolean
  name?: string
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      label,
      value,
      onChange,
      options,
      placeholder = 'Wybierz z listy',
      required = false,
      className = '',
      error,
      disabled = false,
      name,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const selectRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          selectRef.current &&
          !selectRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen)
      }
    }

    const handleOptionSelect = (optionValue: string) => {
      if (onChange) {
        onChange(optionValue)
      }
      setIsOpen(false)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (disabled) return

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleToggle()
      } else if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    const currentOption = flattenItems(options).find((o) => o.value === value)

    return (
      <div className={`${s.selectGroup} ${className}`}>
        {label && (
          <label className={s.label}>
            {label}
            {required && ' *'}
          </label>
        )}
        <div
          ref={selectRef}
          className={`${s.selectContainer} ${disabled ? s.disabled : ''}`}
        >
          <div
            className={`${s.selectTrigger} ${isOpen ? s.open : ''} ${
              error ? s.error : ''
            }`}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span
              className={currentOption ? s.selectValue : s.selectPlaceholder}
            >
              {currentOption ? currentOption.label : placeholder}
            </span>
            <svg
              className={`${s.selectChevron} ${isOpen ? s.rotated : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {isOpen && !disabled && (
            <div className={s.selectDropdown} role="listbox">
              {options.map((item, gi) =>
                isGroup(item) ? (
                  <div key={`g-${gi}`} className={s.selectGroup_dropdown}>
                    <div className={s.selectGroupLabel}>{item.groupLabel}</div>
                    {item.options.map((option) => (
                      <div
                        key={option.value}
                        className={`${s.selectOption} ${s.selectOptionIndented} ${
                          option.value === value ? s.selected : ''
                        }`}
                        onClick={() => handleOptionSelect(option.value)}
                        role="option"
                        aria-selected={option.value === value}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    key={item.value}
                    className={`${s.selectOption} ${
                      item.value === value ? s.selected : ''
                    }`}
                    onClick={() => handleOptionSelect(item.value)}
                    role="option"
                    aria-selected={item.value === value}
                  >
                    {item.label}
                  </div>
                ),
              )}
            </div>
          )}
        </div>
        {error && <span className={s.errorText}>{error}</span>}
      </div>
    )
  },
)

Select.displayName = 'Select'
